class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.addEventListener('click', (event) => {
      if (!this.classList.contains('active')) return;

      const inner = event.target.closest('.drawer__inner');
      const clickedOverlay = event.target.closest('#CartDrawer-Overlay, .cart-drawer__overlay, .mil-cart__overlay');

      if (clickedOverlay || !inner) {
        event.preventDefault();
        this.close();
      }
    });
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartSelectors = [
      '#cart-icon-bubble',
      '.mil-header__cart',
      '[data-mil-cart-drawer-trigger]'
    ];
    const cartLinks = document.querySelectorAll(cartSelectors.join(','));
    if (!cartLinks.length) return;

    cartLinks.forEach((cartLink) => {
      if (!cartLink || cartLink.dataset.cartDrawerBound === 'true') return;

      cartLink.dataset.cartDrawerBound = 'true';
      cartLink.setAttribute('role', 'button');
      cartLink.setAttribute('aria-haspopup', 'dialog');

      cartLink.addEventListener('click', (event) => {
        event.preventDefault();
        this.open(cartLink);
      });

      cartLink.addEventListener('keydown', (event) => {
        if (event.code.toUpperCase() === 'SPACE' || event.code.toUpperCase() === 'ENTER') {
          event.preventDefault();
          this.open(cartLink);
        }
      });
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    this.lockScroll();
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    this.unlockScroll();
  }

  lockScroll() {
    const scrollbarWidth = Math.max(0, window.innerWidth - document.documentElement.clientWidth);
    document.documentElement.style.setProperty('--mil-cart-scrollbar-width', `${scrollbarWidth}px`);
    document.documentElement.classList.add('mil-cart-drawer-lock');
    document.body.classList.add('overflow-hidden', 'mil-cart-drawer-lock');
  }

  unlockScroll() {
    document.documentElement.classList.remove('mil-cart-drawer-lock');
    document.body.classList.remove('overflow-hidden', 'mil-cart-drawer-lock');
    document.documentElement.style.removeProperty('--mil-cart-scrollbar-width');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.setHeaderCartIconAccessibility();
      this.open();
    });

    // Pranešam likusiam puslapiui (pvz. prekės puslapiui), kad krepšelis pasikeitė,
    // kad jis persiskaičiuotų likutį ir atrakintų "Į krepšelį" po ištrynimo/kiekio keitimo.
    document.dispatchEvent(new CustomEvent('cart:refresh'));
    document.dispatchEvent(new CustomEvent('cart:updated', { detail: parsedState }));

    // Paprašom dovanų logikos perskaičiuoti sąlygas po kiekvieno dryžio perpiešimo:
    // jei prekės iš tinkamos kolekcijos siekia 590 – lieka 1 dovana, jei 1200 – dvi;
    // jei riba nebepasiekiama, dovana (data-mil-gift-line) pašalinama automatiškai.
    document.dispatchEvent(new CustomEvent('mil:gift:refresh'));
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

// Fiksuotas "toast" pranešimas – prikabintas prie <body>, su maksimaliu z-index ir
// savais inline stiliais. Nepriklauso nuo temos CSS, nuo dryžio perpiešimo ar
// slinkties, todėl visada matomas. Naudojamas likučio limito info parodymui.
function showLimitToast(msg) {
  if (!msg) return;
  try {
    let toast = document.getElementById('mil-cart-limit-toast');
    if (!toast) {
      toast = document.createElement('div');
      toast.id = 'mil-cart-limit-toast';
      toast.setAttribute('role', 'status');
      toast.setAttribute('aria-live', 'polite');
      toast.style.cssText = [
        'position:fixed',
        'z-index:2147483647',
        'left:50%',
        'top:24px',
        'transform:translateX(-50%)',
        'max-width:90vw',
        'background:#111',
        'color:#fff',
        'padding:12px 18px',
        'border-radius:10px',
        'font-size:14px',
        'line-height:1.35',
        'box-shadow:0 10px 30px rgba(0,0,0,.35)',
        'text-align:center',
        'pointer-events:none',
        'opacity:0',
        'transition:opacity .2s ease'
      ].join(';');
      document.body.appendChild(toast);
    }
    toast.textContent = msg;
    requestAnimationFrame(() => { toast.style.opacity = '1'; });
    clearTimeout(toast._milTimer);
    toast._milTimer = setTimeout(() => {
      toast.style.opacity = '0';
      setTimeout(() => { if (toast && toast.parentNode) toast.parentNode.removeChild(toast); }, 260);
    }, 3200);
  } catch (e) { /* no-op */ }
}

// Lokalizuota limito žinutė (nustatoma cart-drawer.liquid per window.milCartStrings).
function milLimitMessage() {
  return (window.milCartStrings && window.milCartStrings.maxReached) || 'Maximum available quantity reached.';
}

// Parodo limito info visur, kur tik gali: toast + bendra klaidų juosta.
function milNotifyLimit() {
  const msg = milLimitMessage();
  showLimitToast(msg);
  const box = document.getElementById('CartDrawer-CartErrors');
  if (box) {
    box.textContent = msg;
    box.style.cssText = 'display:block;margin:10px 0 0;font-size:13px;line-height:1.35;text-align:center;color:#c0392b;';
  }
}

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }

  // When quantity reaches 0 (Remove button, minus button, or typing 0) Shopify's
  // standard change-flow aborts on the "already sold out" warning and never
  // re-renders, so sold-out items get stuck in the cart. Force a reliable removal.
  updateQuantity(line, quantity, event, name, variantId) {
    if (Number(quantity) === 0) {
      this.milRemoveLine(line);
      return;
    }
    super.updateQuantity(line, quantity, event, name, variantId);
  }

  milRemoveLine(line) {
    if (typeof this.enableLoading === 'function') this.enableLoading(line);

    const removeBtn = document.getElementById(`CartDrawer-Remove-${line}`);
    const lineKey = removeBtn ? removeBtn.dataset.lineKey : null;
    const sections = this.getSectionsToRender().map((section) => section.section);
    const changeUrl = (window.routes && window.routes.cart_change_url) || '/cart/change.js';
    const updateUrl = (window.routes && window.routes.cart_update_url) || '/cart/update.js';
    const cartUrl = (window.routes && window.routes.cart_url) || '/cart';

    const base = { quantity: 0, sections, sections_url: window.location.pathname };

    const renderState = (state) => {
      const cartDrawer = document.querySelector('cart-drawer');
      if (state && state.sections && cartDrawer && typeof cartDrawer.renderContents === 'function') {
        cartDrawer.classList.toggle('is-empty', state.item_count === 0);
        cartDrawer.renderContents(state);
      } else {
        window.location.href = cartUrl;
      }
    };

    // Žiedo dydžio pora: jei ši eilutė (arba jos porininkas) turi bendrą žymenį,
    // ištrinam VISAS tos grupės eilutes vienu kartu (žiedą + paslaugą).
    const itemEl = document.getElementById(`CartDrawer-Item-${line}`);
    const group = itemEl ? itemEl.getAttribute('data-mil-resize-group') : null;
    if (group) {
      const esc = (window.CSS && CSS.escape) ? CSS.escape(group) : group.replace(/["\\]/g, '\\$&');
      const groupKeys = [];
      document.querySelectorAll(`[data-mil-resize-group="${esc}"]`).forEach((el) => {
        const rb = el.querySelector('cart-remove-button');
        const k = rb ? rb.dataset.lineKey : null;
        if (k && groupKeys.indexOf(k) === -1) groupKeys.push(k);
      });
      if (lineKey && groupKeys.indexOf(lineKey) === -1) groupKeys.push(lineKey);

      if (groupKeys.length > 1) {
        const updates = {};
        groupKeys.forEach((k) => { updates[k] = 0; });
        fetch(updateUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ updates, sections, sections_url: window.location.pathname }),
        })
          .then((response) => response.json())
          .catch(() => null)
          .then(renderState)
          .finally(() => {
            if (typeof this.disableLoading === 'function') this.disableLoading(line);
          });
        return;
      }
    }

    const change = (payload) =>
      fetch(changeUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(Object.assign({}, base, payload)),
      })
        .then((response) => response.json())
        .catch(() => null);

    // Prefer removing by line key (works on out-of-stock lines); fall back to line index.
    const firstAttempt = lineKey ? change({ id: lineKey }) : change({ line: Number(line) });

    firstAttempt
      .then((state) => {
        const failed = !state || state.status || state.errors || !state.sections;
        // If the key-based attempt reported an issue, retry by line index.
        if (failed && lineKey) return change({ line: Number(line) });
        return state;
      })
      .then(renderState)
      .finally(() => {
        if (typeof this.disableLoading === 'function') this.disableLoading(line);
      });
  }

  // Nustato eilutės kiekį per /cart/change.js (aplenkia temos quantity-input komponentą
  // ir jo validaciją, kurie šiame dryžyje neveikdavo su "+"). Likutį užtikrina Shopify:
  // jei prašomas kiekis viršija likutį (deny politika), grąžinama 422 klaida – ją
  // parodom eilutės klaidos vietoje, kiekio nekeičiam.
  milSetQuantity(line, quantity) {
    const qty = Math.max(0, Number(quantity) || 0);
    if (qty === 0) { this.milRemoveLine(line); return; }

    if (typeof this.enableLoading === 'function') this.enableLoading(line);

    const removeBtn = document.getElementById(`CartDrawer-Remove-${line}`);
    const lineKey = removeBtn ? removeBtn.dataset.lineKey : null;
    const sections = this.getSectionsToRender().map((section) => section.section);
    const changeUrl = (window.routes && window.routes.cart_change_url) || '/cart/change.js';

    const showLineError = (msg) => {
      const wrap = document.getElementById(`CartDrawer-LineItemError-${line}`);
      const textEl = wrap ? wrap.querySelector('.cart-item__error-text') : null;
      if (textEl) textEl.textContent = msg || '';
    };

    // Info apie pasiektą limitą rodom patikimoje, visada matomoje vietoje –
    // bendroje krepšelio klaidų juostoje (CartDrawer-CartErrors), su savo inline
    // stiliumi, kad nepriklausytų nuo temos CSS (eilutės klaidos blokas dažnai
    // būna paslėptas). Papildomai užpildom ir eilutės klaidos tekstą.
    const showLimitNote = (msg) => {
      showLimitToast(msg);
      showLineError(msg);
      const box = document.getElementById('CartDrawer-CartErrors');
      if (!box) return;
      box.textContent = msg || '';
      if (msg) {
        box.style.display = 'block';
        box.style.margin = '10px 0 0';
        box.style.fontSize = '13px';
        box.style.lineHeight = '1.35';
        box.style.textAlign = 'center';
        box.style.color = '#c0392b';
      } else {
        box.removeAttribute('style');
      }
    };

    // Trumpa, lokalizuota info kai pasiekiamas turimo likučio limitas.
    const limitInfo = (window.milCartStrings && window.milCartStrings.maxReached) || 'Maximum available quantity reached.';

    // Ar serveris apkarpė kiekį dėl likučio? /cart/change.js grąžina 200 ir tyliai
    // nustato kiekį iki turimo, tad palyginam grąžintą eilutės kiekį su prašytu.
    const maybeShowCapNote = (state) => {
      if (!state || !Array.isArray(state.items)) return;
      let item = null;
      if (lineKey) item = state.items.find((it) => it && it.key === lineKey) || null;
      if (!item) {
        const idx = Number(line) - 1;
        if (idx >= 0 && idx < state.items.length) item = state.items[idx];
      }
      if (item && Number(item.quantity) < qty) {
        showLimitNote(limitInfo);
      }
    };

    const renderState = (state) => {
      const cartDrawer = document.querySelector('cart-drawer');
      if (state && state.sections && cartDrawer && typeof cartDrawer.renderContents === 'function') {
        cartDrawer.classList.toggle('is-empty', state.item_count === 0);
        cartDrawer.renderContents(state);
        return true;
      }
      return false;
    };

    const change = (payload) =>
      fetch(changeUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(Object.assign({ quantity: qty, sections, sections_url: window.location.pathname }, payload)),
      })
        .then((response) => response.json())
        .catch(() => null);

    // Keičiam pagal eilutės indeksą; jei nepavyktų – bandom pagal line key.
    change({ line: Number(line) })
      .then((state) => {
        if (renderState(state)) {
          // Sėkmingai perpiešta – bet patikrinam, ar kiekis nebuvo apkarpytas.
          maybeShowCapNote(state);
          return;
        }
        // Nėra sekcijų: arba likučio klaida (422), arba reikia bandyti per key.
        if (state && (state.status || state.description || state.message)) {
          showLimitNote(limitInfo);
          return;
        }
        if (lineKey) {
          return change({ id: lineKey }).then((s2) => {
            if (renderState(s2)) { maybeShowCapNote(s2); return; }
            if (s2 && (s2.status || s2.description || s2.message)) showLimitNote(limitInfo);
          });
        }
      })
      .finally(() => {
        if (typeof this.disableLoading === 'function') this.disableLoading(line);
      });
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);

(function () {
  function setButtonState(button, isLoading) {
    if (!button) return;
    button.disabled = isLoading;
    button.classList.toggle('is-loading', isLoading);
  }

  function showCartError(message) {
    const target = document.getElementById('CartDrawer-CartErrors');
    if (!target) return;
    // Tuščia/nenurodyta žinutė = klaidos laukelį išvalom.
    target.textContent = message ? message : '';
  }

  function clearCartError() {
    showCartError('');
  }

  async function addVariantToCart(button) {
    const variantId = button && button.getAttribute('data-mil-cart-add-variant');
    if (!variantId) return;

    setButtonState(button, true);
    clearCartError();

    const sections = ['cart-drawer', 'cart-icon-bubble'];
    const body = JSON.stringify({
      items: [{ id: Number(variantId), quantity: 1 }],
      sections: sections,
      sections_url: window.location.pathname
    });

    try {
      const response = await fetch(window.routes.cart_add_url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body
      });

      const parsed = await response.json();
      if (!response.ok || parsed.status) {
        throw new Error(parsed.description || parsed.message || window.cartStrings?.error || 'Cart error');
      }

      const cartDrawer = document.querySelector('cart-drawer');
      if (cartDrawer && typeof cartDrawer.renderContents === 'function') {
        cartDrawer.renderContents(parsed);
      } else {
        window.location.href = window.routes.cart_url;
      }
    } catch (error) {
      showCartError(error.message || window.cartStrings?.error || 'Cart error');
      setButtonState(button, false);
    }
  }

  document.addEventListener('click', function (event) {
    const button = event.target.closest('[data-mil-cart-add-variant]');
    if (!button) return;
    event.preventDefault();
    if (button.disabled || button.classList.contains('is-loading')) return;
    addVariantToCart(button);
  });
})();

(function () {
  // Perimame IR "+", IR "−" cart dryžyje ir tvarkome kiekį patys per tiesioginį
  // /cart/change (milSetQuantity / milRemoveLine). Taip aplenkiam temos
  // quantity-input komponentą ir jo validaciją, dėl kurių "+" neveikdavo.
  document.addEventListener(
    'click',
    function (event) {
      const button = event.target.closest(
        'cart-drawer-items quantity-input button[name="plus"], cart-drawer-items quantity-input button[name="minus"]'
      );
      if (!button || button.disabled) return;

      const quantityWrapper = button.closest('quantity-input');
      const quantityInput = quantityWrapper && quantityWrapper.querySelector('input.quantity__input');
      if (!quantityInput) return;

      const cartItems = button.closest('cart-drawer-items');
      const line = quantityInput.dataset.index;
      if (!cartItems || !line) return;

      const currentValue = parseInt(quantityInput.value, 10);
      if (!Number.isFinite(currentValue)) return;
      const step = Math.max(1, parseInt(quantityInput.step, 10) || 1);
      const isPlus = button.name === 'plus';
      const target = isPlus ? currentValue + step : currentValue - step;

      // Neleidžiam komponentui apdoroti šio paspaudimo.
      event.preventDefault();
      event.stopImmediatePropagation();

      // Kliento pusės likučio patikra (kaip prekės puslapyje): jei "+" peržengtų
      // likutį, iš karto parodom info ir NEsiunčiam užklausos. Nepriklauso nuo
      // serverio atsakymo formos, tad žinutė visada matoma.
      if (isPlus) {
        const stockTracked = quantityInput.getAttribute('data-mil-stock-tracked') === 'true';
        const stockQty = parseInt(quantityInput.getAttribute('data-mil-stock-qty'), 10);
        if (stockTracked && Number.isFinite(stockQty) && target > stockQty) {
          milNotifyLimit();
          return;
        }
      }

      if (target <= 0) {
        if (typeof cartItems.milRemoveLine === 'function') cartItems.milRemoveLine(line);
        return;
      }
      if (typeof cartItems.milSetQuantity === 'function') {
        cartItems.milSetQuantity(line, target);
      }
    },
    true // capture faze – suveikia anksčiau už paties komponento klausytoją
  );
})();